import { createContext, useEffect, useRef, useState } from 'react';
import DateForm from '../components/activity/createActivity/DateForm';
import DurationForm from '../components/activity/createActivity/DurationForm';
import MoreOptionsForm from '../components/activity/createActivity/moreOptions/MoreOptionsForm';
import TaskCreated from '../components/activity/createActivity/TaskCreated';
import TitleForm from '../components/activity/createActivity/TitleForm';
import Subtasks from '../components/activity/createActivity/moreOptions/Subtasks/Subtasks';
import EditActivityForm from '../components/activity/editActivity/EditActivityForm';
import ActivityEdited from '../components/activity/editActivity/ActivityEdited';
import ErrorView from '../components/utils/ErrorView';
import { useNavigate, useParams } from 'react-router-dom';

export enum ActivityForms {
  Title = 'Title',
  Date = 'Date',
  Duration = 'Duration',
  StarValue = 'StarValue',
  Created = 'Created',
  MoreOptions = 'MoreOptions',
  AddSubtasks = 'AddSubtasks',
  Edit = 'Edit',
  EditInstance = 'EditInstance',
  Edited = 'Edited',
}

export type ActivityForm = {
  component: (props: any) => JSX.Element;
  formId: string;
  formName: string;
  nextForm?: ActivityForms | 'Complete';
  nextFormText?: string;
  nextFormAction?: 'Create' | 'Edit' | 'EditSingleActivity' | 'AddSubtasks' | 'AddRecurrence';
  additionalForm?: ActivityForms;
  additionalFormText?: string;
  progressValue?: number;
  // Antal steg i progress-indikatorn. Utelämnat = 3 (Title/Date/Duration), dvs
  // dagens wizard. Stjärn-personas får 4 (+ StarValue) via starActivityFormsDefs.
  progressTotal?: number;
  isValid?: boolean;
  noHeader?: boolean;
  noActivityName?: boolean;
  noFormName?: boolean;
  logicalPreviousForm?: ActivityForms;
};

export type ComponentLookupTable = {
  [key: string]: ActivityForm;
};

export const DefaultActivityFormsDefs: ComponentLookupTable = {
  Title: {
    component: TitleForm,
    formId: 'Title',
    formName: 'Title',
    nextForm: ActivityForms.Date,
    nextFormText: 'Next: Date & Time',
    progressValue: 1,
    isValid: false,
    noFormName: true,
    noActivityName: true,
  },
  Date: {
    component: DateForm,
    formId: 'Date',
    formName: 'Date & Time',
    nextForm: ActivityForms.Duration,
    nextFormText: 'Next: Duration',
    progressValue: 2,
    isValid: false,
    logicalPreviousForm: ActivityForms.Title,
  },
  Duration: {
    component: DurationForm,
    formId: 'Duration',
    formName: 'Duration',
    nextForm: ActivityForms.Created,
    nextFormText: 'Create activity',
    nextFormAction: 'Create',
    additionalForm: ActivityForms.MoreOptions,
    additionalFormText: 'Details',
    progressValue: 3,
    isValid: false,
    logicalPreviousForm: ActivityForms.Date,
  },
  MoreOptions: {
    component: MoreOptionsForm,
    formId: 'MoreOptions',
    formName: 'Details',
    nextForm: ActivityForms.Created,
    nextFormText: 'Create activity',
    nextFormAction: 'Create',
    noHeader: true,
    isValid: true,
    logicalPreviousForm: ActivityForms.Duration,
  },
  AddSubtasks: {
    component: Subtasks,
    formId: 'AddSubtasks',
    formName: 'Add subtasks',
    noHeader: true,
    isValid: true,
    logicalPreviousForm: ActivityForms.MoreOptions,
  },
  Created: {
    component: TaskCreated,
    formId: 'Created',
    formName: 'Activity created',
    noHeader: true,
  },
  Edit: {
    component: EditActivityForm,
    formId: 'Edit',
    formName: 'Edit',
    nextForm: ActivityForms.Edited,
    nextFormAction: 'Edit',
    nextFormText: 'Update activity',
    noHeader: true,
    noActivityName: true,
    isValid: false,
  },
  Edited: {
    component: ActivityEdited,
    formId: 'Edited',
    formName: 'Activity created',
    noHeader: true,
  },
};

export type ActivityFormContextType = {
  goToPreviousForm: () => void;
  goToNextForm: () => void;
  goToForm: (form: ActivityForms | 'Back') => void;
  setValid: (value: boolean) => void;
  setFormError: (error: string | null) => void;
  current: ActivityForm;
  assistantAnchorRef: React.RefObject<HTMLButtonElement>;
  assistantAnchorEl?: HTMLElement | null;
  setAssistantAnchorEl?: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  showAssistantBadge?: boolean;
  setShowAssistantBadge?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ActivityFormContext = createContext<ActivityFormContextType>(
  {} as ActivityFormContextType,
);

type props = {
  children?: React.ReactNode;
  initialForm?: ActivityForms;
  formOverrideConfig?: ComponentLookupTable;
};

const ActivityFormScene = ({
  children,
  initialForm = ActivityForms.Title,
  formOverrideConfig: activityFormsDefs = DefaultActivityFormsDefs,
}: props) => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [form, setForm] = useState<ActivityForm>(activityFormsDefs[initialForm]);
  const [formError, setFormError] = useState<string | null>(null);
  const assistantAnchorRef = useRef<HTMLButtonElement>(null);
  const [assistantAnchorEl, setAssistantAnchorEl] = useState<null | HTMLElement>(null);
  const [showAssistantBadge, setShowAssistantBadge] = useState<boolean>(false);

  const previousForm = () => {
    if (form.logicalPreviousForm) {
      navigateToForm(form.logicalPreviousForm);
    } else {
      const url = window.location.pathname;
      const urlParts = url.split('/');
      const parentUrl = urlParts.slice(0, urlParts.length - 2).join('/');
      navigate(parentUrl || '/');
    }
  };

  const nextForm = () => {
    if (form.nextForm) navigateToForm(form.nextForm);
  };

  const goToForm = (newForm: ActivityForms | 'Back') => {
    if (newForm === 'Back') {
      previousForm();
    } else {
      navigateToForm(newForm);
    }
  };

  const navigateToForm = (form: string) => {
    const url = window.location.pathname;
    const urlParts = url.split('/');
    const newUrl = urlParts.slice(0, urlParts.length - 1).join('/');
    navigate(newUrl + '/' + form, { replace: true });
  };

  const setValid = (value: boolean) => setForm({ ...form, isValid: value });

  useEffect(() => {
    if (formId) {
      const newForm = activityFormsDefs[formId as ActivityForms];
      if (newForm) {
        setForm(newForm);
      }
    }
  }, [formId]);

  useEffect(() => {
    setAssistantAnchorEl(null);
  }, [navigate]);

  return (
    <ActivityFormContext.Provider
      value={{
        goToPreviousForm: previousForm,
        goToNextForm: nextForm,
        goToForm: goToForm,
        current: form,
        setValid: setValid,
        setFormError: setFormError,
        assistantAnchorRef: assistantAnchorRef,
        assistantAnchorEl: assistantAnchorEl,
        setAssistantAnchorEl: setAssistantAnchorEl,
        showAssistantBadge: showAssistantBadge,
        setShowAssistantBadge: setShowAssistantBadge,
      }}
    >
      {formError ? (
        <ErrorView error={formError} onTryAgain={() => setFormError(null)} />
      ) : (
        <>{children}</>
      )}
    </ActivityFormContext.Provider>
  );
};

export default ActivityFormScene;
